// src/pages/HomePage.js - React Router対応版
import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useLocalizedNav } from '../i18n/useLocalizedNav';
import { pick, formatNewsDate } from '../i18n/pick';
import { useDocumentMeta } from '../i18n/useDocumentMeta';
import { newsData } from '../data/newsData';
import { CONTACT } from '../data/inboundBusinessData';
import { heroSliderData } from '../data/heroSliderData';
import HeroSlider from '../components/HeroSlider';
import AnimatedSection from '../components/AnimatedSection';
import { 
  COLORS, 
  FONT_SIZES, 
  FONT_WEIGHTS, 
  SPACING, 
  BORDER_RADIUS, 
  SHADOWS, 
  TRANSITIONS,
  LAYOUT,
  COMPONENTS,
  MOBILE_STYLES 
} from '../styles/constants';
import { applyCardHover, removeCardHover } from '../utils/hoverEffects';

// 取引先向けエクストラネット（別アプリ）の入口。こちらにはリンクだけを置く。
const PARTNER_LOGIN_URL = 'https://jtc-net.co.jp/jtc-partner/login.php';

/**
 * 文の中の「こちら」だけをリンクにするための部品。
 *
 * **children を受け取る形にしてある。**文字は翻訳ファイルにあり、
 * Trans が実行時に差し込む。それを見込んで <a> を空で書くと、
 * ESLint の jsx-a11y/anchor-has-content が警告を出し、
 * **CI=true のビルドがそれで落ちる**。
 *
 * 見た目をここに置いているのは、styles が HomePage の中にあるため。
 * 部品を中に作ると、描画のたびに別の部品になって作り直される。
 */
const MailLink = ({ children }) => (
  <a
    href={`mailto:${CONTACT.email}`}
    style={{
      // **下線を引く。**文の中の2文字だけがリンクなので、
      // 色を変えるだけだと押せると分からない。
      color: COLORS.link,
      fontWeight: FONT_WEIGHTS.bold,
      textDecoration: 'underline',
    }}
  >
    {children}
  </a>
);


const HomePage = () => {
  const { t } = useTranslation();
  const { lang, navigate } = useLocalizedNav();
  useDocumentMeta('home');

  const styles = {
    newsSection: {
      padding: LAYOUT.section.padding,
      backgroundColor: COLORS.backgroundSecondary,
    },
    container: LAYOUT.container,
    sectionTitle: {
      fontSize: FONT_SIZES.title,
      fontWeight: FONT_WEIGHTS.bold,
      textAlign: 'center',
      marginBottom: SPACING.xxxl,
      color: COLORS.textPrimary,
    },
    newsList: {},
    newsItem: {
      display: 'flex',
      alignItems: 'center',
      padding: `${SPACING.md} ${SPACING.lg}`,
      borderBottom: `1px solid ${COLORS.backgroundTertiary}`,
      gap: SPACING.md,
      cursor: 'pointer',
      transition: TRANSITIONS.base,
    },
    newsDate: {
      color: COLORS.textTertiary,
      fontSize: FONT_SIZES.sm,
      minWidth: '100px',
    },
    newsCategory: {
      backgroundColor: COLORS.categoryBackground,
      color: COLORS.categoryText,
      padding: `4px ${SPACING.sm}`,
      borderRadius: BORDER_RADIUS.sm,
      fontSize: FONT_SIZES.xs,
      fontWeight: FONT_WEIGHTS.bold,
      width: '120px',
    },
    newsTitle: {
      color: COLORS.textPrimary,
      fontSize: FONT_SIZES.base,
      flex: 1,
      textAlign: 'left',
    },
    newsButtonContainer: {
      textAlign: 'center',
      marginTop: SPACING.lg,
    },
    newsButton: {
      ...COMPONENTS.button.base,
      ...COMPONENTS.button.secondary,
    },
    workSection: {
      padding: LAYOUT.section.padding,
      backgroundColor: 'rgb(82, 154, 197)',
    },
    workContent: {
      textAlign: 'center',
    },
    workTitle: {
      fontSize: FONT_SIZES.xxxl,
      fontWeight: FONT_WEIGHTS.bold,
      marginBottom: SPACING.md,
      color: COLORS.textPrimary,
    },
    workDescription: {
      fontSize: FONT_SIZES.base,
      color: COLORS.textTertiary,
      lineHeight: '1.8',
      marginBottom: SPACING.xxxl,
      maxWidth: '800px',
      margin: `0 auto ${SPACING.xxxl}`,
    },
    workGrid: {
      display: 'grid',
      // 左（インバウンド）を 2、右（音声ガイド）を 1。
      // 1024px 以下では左の中身が縦に積み直る（下の CSS）。
      gridTemplateColumns: '2fr 1fr',
      gap: SPACING.lg,
      marginTop: SPACING.xl,
      alignItems: 'stretch',
    },
    workCard: {
      ...COMPONENTS.card.base,
      textAlign: 'left',
      backgroundColor: COLORS.backgroundSecondary,
      // **2枚の高さをそろえる。**
      // グリッドの入れ物（AnimatedSection）は既に
      // 行の高さまで伸びているので、中身を 100% にすれば揃う。
      // （box-sizing: border-box は App.css で全体にかかっている）
      height: '100%',
    },
    workCardTitle: {
      fontSize: FONT_SIZES.xl,
      fontWeight: FONT_WEIGHTS.bold,
      marginBottom: SPACING.base,
      color: COLORS.primary,
    },
    workCardText: {
      fontSize: FONT_SIZES.base,
      color: COLORS.textTertiary,
      lineHeight: '1.7',
    },
    workCardMore: {
      display: 'inline-block',
      marginTop: SPACING.base,
      color: '#d8382b',
      fontWeight: FONT_WEIGHTS.bold,
      fontSize: FONT_SIZES.sm,
    },
    inboundSplit: {
      display: 'flex',
      alignItems: 'center',
      gap: SPACING.lg,
    },
    inboundMain: {
      flex: '1 1 auto',
      // 長い文章が桁を押し広げないようにする。
      // これが無いと flex の子は縮まない。
      minWidth: 0,
    },
    inboundMailText: {
      marginTop: SPACING.base,
      fontSize: FONT_SIZES.sm,
      color: COLORS.textTertiary,
      lineHeight: '1.7',
    },
    inboundSide: {
      flex: '0 0 230px',
      borderLeft: `1px solid ${COLORS.backgroundTertiary}`,
      paddingLeft: SPACING.lg,
    },
    partnerLoginButton: {
      display: 'block',
      padding: `${SPACING.base} ${SPACING.md}`,
      backgroundColor: COLORS.primary,
      color: COLORS.backgroundPrimary,
      borderRadius: BORDER_RADIUS.md,
      fontSize: FONT_SIZES.sm,
      fontWeight: FONT_WEIGHTS.bold,
      lineHeight: '1.6',
      textAlign: 'center',
      textDecoration: 'none',
      boxShadow: SHADOWS.base,
      transition: TRANSITIONS.base,
    },
    partnerLoginFor: {
      // **2行目を折り返しに任せない。**
      // 1つの文字列にすると、日本語は語の途中
      // （「旅人倶楽／部ログイン」）で折れる。
      display: 'block',
      fontSize: FONT_SIZES.xs,
      fontWeight: FONT_WEIGHTS.normal,
      opacity: 0.85,
      marginBottom: '2px',
    },
    contactSection: {
      padding: LAYOUT.section.padding,
      backgroundColor: COLORS.backgroundSecondary,
    },
    contactContent: {
      maxWidth: '800px',
      margin: '0 auto',
    },
    contactInfo: {
      textAlign: 'center',
      backgroundColor: COLORS.backgroundPrimary,
      padding: SPACING.lg,
      borderRadius: BORDER_RADIUS.md,
      boxShadow: SHADOWS.base,
    },
    contactInfoItem: {
      marginBottom: SPACING.base,
      fontSize: FONT_SIZES.base,
      color: COLORS.textSecondary,
    },

    // AYUBITO 紹介セクション（和モダン：紺＋赤＋金）
    ayubitoSection: {
      padding: LAYOUT.section.padding,
      backgroundColor: COLORS.backgroundSecondary,
    },
    ayubitoBand: {
      backgroundColor: '#ffffff',
      borderRadius: BORDER_RADIUS.lg,
      boxShadow: SHADOWS.md,
      padding: SPACING.xl,
      display: 'grid',
      gridTemplateColumns: '1.05fr 0.95fr',
      gap: SPACING.xl,
      alignItems: 'center',
      overflow: 'hidden',
    },
    ayubitoBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: SPACING.sm,
      backgroundColor: '#fdecea',
      color: '#d8382b',
      fontWeight: FONT_WEIGHTS.bold,
      fontSize: FONT_SIZES.sm,
      borderRadius: BORDER_RADIUS.full,
      padding: `8px ${SPACING.base}`,
      marginBottom: SPACING.md,
    },
    ayubitoBadgeRel: {
      color: '#3a4d78',
    },
    ayubitoHead: {
      fontSize: FONT_SIZES.xxl,
      fontWeight: FONT_WEIGHTS.bold,
      color: '#14274e',
      lineHeight: '1.4',
      margin: 0,
      textAlign: 'left',
    },
    ayubitoRed: {
      color: '#d8382b',
    },
    ayubitoWord: {
      fontSize: '40px',
      fontWeight: FONT_WEIGHTS.bold,
      letterSpacing: '0.1em',
      color: '#14274e',
      margin: `${SPACING.xs} 0 ${SPACING.base}`,
      textAlign: 'left',
    },
    ayubitoDesc: {
      fontSize: FONT_SIZES.base,
      color: '#3a4d78',
      lineHeight: '1.8',
      marginBottom: SPACING.lg,
      textAlign: 'left',
    },
    ayubitoFeats: {
      display: 'flex',
      gap: SPACING.lg,
      marginBottom: SPACING.lg,
      flexWrap: 'wrap',
    },
    ayubitoFeat: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: SPACING.xs,
      width: '110px',
      textAlign: 'center',
    },
    ayubitoFeatIcon: {
      width: '52px',
      height: '52px',
      borderRadius: BORDER_RADIUS.circle,
      border: '1.5px solid #dbe2f0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#14274e',
    },
    ayubitoFeatTitle: {
      fontSize: FONT_SIZES.sm,
      fontWeight: FONT_WEIGHTS.bold,
      color: '#14274e',
    },
    ayubitoFeatSub: {
      fontSize: FONT_SIZES.xs,
      color: COLORS.textTertiary,
      lineHeight: '1.5',
    },
    ayubitoCta: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: SPACING.sm,
      backgroundColor: '#14274e',
      color: '#ffffff',
      fontWeight: FONT_WEIGHTS.bold,
      fontSize: FONT_SIZES.base,
      padding: `14px ${SPACING.lg}`,
      borderRadius: BORDER_RADIUS.full,
      textDecoration: 'none',
      transition: TRANSITIONS.base,
    },
    ayubitoImageWrap: {
      display: 'flex',
      justifyContent: 'center',
    },
    ayubitoImage: {
      width: '100%',
      maxWidth: '440px',
      height: 'auto',
      display: 'block',
    },

  };

  return (
    <div>
      <style>
        {MOBILE_STYLES}
        {`
          @media (max-width: 768px) {
            .news-item-mobile {
              flex-direction: column !important;
              align-items: flex-start !important;
              gap: ${SPACING.sm} !important;
            }
            .news-date-mobile {
              min-width: auto !important;
            }
            .news-category-mobile {
              width: auto !important;
            }
            .news-title-mobile {
              font-size: ${FONT_SIZES.sm} !important;
            }
            .ayubito-band-mobile {
              grid-template-columns: 1fr !important;
              text-align: center !important;
              padding: 28px 20px !important;
            }
            .ayubito-band-mobile h3,
            .ayubito-band-mobile p,
            .ayubito-word-mobile {
              text-align: center !important;
            }
            .ayubito-feats-mobile {
              justify-content: center !important;
            }
            .ayubito-word-mobile {
              font-size: 32px !important;
            }
          }
          /*
            左右に割ったままだと、タブレット幅で本文が
            200px 弱まで絞られて読めなくなる。先に縦へ積む。
            値は直書きしているので !important が要る。
          */
          @media (max-width: 1024px) {
            .work-inbound-split {
              flex-direction: column !important;
              align-items: stretch !important;
            }
            .work-inbound-side {
              flex: 1 1 auto !important;
              border-left: none !important;
              border-top: 1px solid ${COLORS.backgroundTertiary} !important;
              padding-left: 0 !important;
              padding-top: ${SPACING.md} !important;
            }
          }
        `}
      </style>

      {/* ヒーロースライダー */}
      <HeroSlider slides={heroSliderData} />
  
      {/* 新着情報セクション */}
      <section style={styles.newsSection}>
        <div style={styles.container}>
          <AnimatedSection animationType="fadeUp" delay={0}>
            <h2 style={styles.sectionTitle} className="mobile-text-title">
              {t('home.news.title')}
            </h2>
          </AnimatedSection>

          <AnimatedSection animationType="fadeUp" delay={0.2}>
            <div style={styles.newsList}>
              {newsData.slice().reverse().slice(0, 3).map((news, index) => (
                <AnimatedSection
                  key={news.id}
                  animationType="fadeUp"
                  delay={0.1 * index}
                  duration={0.6}
                >
                  <div
                    style={styles.newsItem}
                    className="news-item-mobile mobile-spacing-md"
                    onClick={() => navigate(`/news/${news.id}`)}
                    onMouseEnter={(e) => applyCardHover(e)}
                    onMouseLeave={(e) => removeCardHover(e)}
                  >
                    <div style={styles.newsDate} className="news-date-mobile">
                      {formatNewsDate(news.date, lang)}
                    </div>
                    <div style={styles.newsCategory} className="news-category-mobile">
                      {pick(news.category, lang)}
                    </div>
                    <div style={styles.newsTitle} className="news-title-mobile">
                      {pick(news.title, lang)}
                    </div>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </AnimatedSection>

          <AnimatedSection animationType="fadeUp" delay={0.4}>
            <div style={styles.newsButtonContainer}>
              <button onClick={() => navigate('/news')} style={styles.newsButton}>
                {t('home.news.more')} →
              </button>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* WORKセクション */}
      <section id="work" style={styles.workSection}>
        <div style={styles.container}>
          <AnimatedSection animationType="fadeUp" delay={0}>
            <h2 style={styles.sectionTitle} className="mobile-text-title">WORKS</h2>
          </AnimatedSection>

          <div style={styles.workContent}>
            <div style={styles.workGrid} className="mobile-grid-single mobile-gap-md">
              {/*
                **詳細ページ（/business/inbound）への道は、まだ作らない。**
                ページ自体は残してあるが公開前なので、
                「詳しく見る」だけでなく**カードを押したときの遷移も外す**。
                文字だけ消して押せるままにすると、見えないリンクになる。

                公開するときは onClick と cursor を戻す。そのとき
                **中の2つのリンクに `onClick={(e) => e.stopPropagation()}` を足すこと**。
                足さないと、ログインやメールを押した瞬間に
                詳細ページへ切り替わって、どちらへも行けない。
              */}
              <AnimatedSection animationType="fadeUp" delay={0.2}>
                <div style={styles.workCard} className="mobile-spacing-md">
                  <div style={styles.inboundSplit} className="work-inbound-split">
                    {/* 左：事業の説明と、掲載・利用希望の問い合わせ */}
                    <div style={styles.inboundMain}>
                      <h4 style={styles.workCardTitle}>{t('home.work.inbound.title')}</h4>
                      <p style={styles.workCardText}>
                        {t('home.work.inbound.text')}
                      </p>
                      {/*
                        文の中の「こちら」だけをリンクにする。
                        日本語と英語でリンクの位置が違うので、
                        **文を前後に切らない**（切ると語順の違う言語で崩れる）。
                      */}
                      <div style={styles.inboundMailText}>
                        <Trans
                          i18nKey="home.work.inbound.listingContact"
                          components={{ lnk: <MailLink /> }}
                        />
                      </div>
                    </div>

                    {/* 右：取引先向けエクストラネットの入口 */}
                    <div style={styles.inboundSide} className="work-inbound-side">
                      <a
                        href={PARTNER_LOGIN_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={styles.partnerLoginButton}
                      >
                        <span style={styles.partnerLoginFor}>
                          {t('home.work.inbound.partnerLoginFor')}
                        </span>
                        {t('home.work.inbound.partnerLogin')}
                      </a>
                    </div>
                  </div>
                </div>
              </AnimatedSection>

              <AnimatedSection animationType="fadeUp" delay={0.4}>
                <a
                  href="https://web.ayubito.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: 'none', display: 'block', height: '100%' }}
                >
                  <div
                    style={styles.workCard}
                    className="mobile-spacing-md"
                    onMouseEnter={(e) => applyCardHover(e)}
                    onMouseLeave={(e) => removeCardHover(e)}
                  >
                    <h4 style={styles.workCardTitle}>{t('home.work.guide.title')}</h4>
                    <p style={styles.workCardText}>
                      {t('home.work.guide.text')}
                    </p>
                    <span style={styles.workCardMore}>
                      {t('home.work.guide.more')} →
                    </span>
                  </div>
                </a>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>

      {/* AYUBITO 紹介セクション */}
      <section style={styles.ayubitoSection}>
        <div style={styles.container}>
          <AnimatedSection animationType="fadeUp" delay={0}>
            <div style={styles.ayubitoBand} className="ayubito-band-mobile">
              {/* 左：コピー */}
              <div>
                <div style={styles.ayubitoBadge}>
                  {t('ayubito.badge')}
                  <span style={styles.ayubitoBadgeRel}>{t('ayubito.release')}</span>
                </div>
                <h3 style={styles.ayubitoHead}>
                  <Trans
                    i18nKey="ayubito.headline"
                    components={{ hl: <span style={styles.ayubitoRed} />, br: <br /> }}
                  />
                </h3>
                <div style={styles.ayubitoWord} className="ayubito-word-mobile">AYUBITO</div>
                <p style={styles.ayubitoDesc}>
                  {t('ayubito.desc')}
                </p>
                <div style={styles.ayubitoFeats} className="ayubito-feats-mobile">
                  <div style={styles.ayubitoFeat}>
                    <div style={styles.ayubitoFeatIcon}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                        <circle cx="12" cy="12" r="9" />
                        <path d="M3 12h18" />
                        <path d="M12 3c2.5 2.5 2.5 15 0 18" />
                        <path d="M12 3c-2.5 2.5-2.5 15 0 18" />
                      </svg>
                    </div>
                    <div style={styles.ayubitoFeatTitle}>{t('ayubito.feature1.title')}</div>
                    <div style={styles.ayubitoFeatSub}>{t('ayubito.feature1.sub')}</div>
                  </div>
                  <div style={styles.ayubitoFeat}>
                    <div style={styles.ayubitoFeatIcon}>
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3 3h7v7H3V3zm2 2v3h3V5H5z" />
                        <path d="M14 3h7v7h-7V3zm2 2v3h3V5h-3z" />
                        <path d="M3 14h7v7H3v-7zm2 2v3h3v-3H5z" />
                        <path d="M14 14h3v3h-3zM18 18h3v3h-3zM14 18h2v3h-2zM18 14h3v2h-3z" />
                      </svg>
                    </div>
                    <div style={styles.ayubitoFeatTitle}>{t('ayubito.feature2.title')}</div>
                    <div style={styles.ayubitoFeatSub}>{t('ayubito.feature2.sub')}</div>
                  </div>
                  <div style={styles.ayubitoFeat}>
                    <div style={styles.ayubitoFeatIcon}>
                      <svg width="22" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                        <path d="M12 22s7-6.5 7-12A7 7 0 0 0 5 10c0 5.5 7 12 7 12z" />
                        <circle cx="12" cy="10" r="2.5" />
                      </svg>
                    </div>
                    <div style={styles.ayubitoFeatTitle}>{t('ayubito.feature3.title')}</div>
                    <div style={styles.ayubitoFeatSub}>{t('ayubito.feature3.sub')}</div>
                  </div>
                </div>
                <a
                  href="https://web.ayubito.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={styles.ayubitoCta}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#0e1b39'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#14274e'; }}
                >
                  {t('ayubito.cta')} →
                </a>
              </div>

              {/* 右：ビジュアル */}
              <div style={styles.ayubitoImageWrap}>
                <img
                  src={process.env.PUBLIC_URL + '/hero05-1.png'}
                  alt={t('ayubito.imageAlt')}
                  style={styles.ayubitoImage}
                />
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>


      {/* CONTACTセクション */}
      <section id="contact" style={styles.contactSection}>
        <div style={styles.container}>
          <AnimatedSection animationType="fadeUp" delay={0}>
            <h2 style={styles.sectionTitle} className="mobile-text-title">CONTACT</h2>
          </AnimatedSection>

          <AnimatedSection animationType="fadeUp" delay={0.2}>
            <div style={styles.contactContent}>
              <div style={styles.contactInfo} className="mobile-spacing-md">
                <div style={styles.contactInfoItem} className="mobile-text-sm">
                  <strong>{t('home.contact.phoneLabel')}</strong> 06-6357-3668
                </div>
                <div style={styles.contactInfoItem} className="mobile-text-sm">
                  <strong>{t('home.contact.hoursLabel')}</strong> {t('home.contact.hours')}
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default HomePage;